/**
 * Subida y lectura de imágenes (§9.3).
 *
 * El bucket es **privado** y las imágenes se sirven con URLs firmadas de dos horas: una imagen de
 * una pregunta puede contener el enunciado de un examen, y un bucket público sería un enlace
 * permanente y adivinable a él.
 *
 * La conversión ocurre **antes**, en `convert.ts`. Aquí solo se sube lo ya convertido.
 */

import { supabase } from '../auth/supabase.js';
import { convertImage, type ConversionError, type ConvertedImage } from './convert.js';

/** El bucket lleva prefijo `clica-`, como exige la convención de la instancia compartida. */
export const BUCKET = 'clica-quiz-images';

/** Dos horas: dura una clase entera sin que nadie tenga que recargar. */
export const SIGNED_URL_SECONDS = 2 * 60 * 60;

export interface UploadedImage {
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
}

export type UploadResult =
  | { readonly ok: true; readonly image: UploadedImage }
  | { readonly ok: false; readonly reason: ConversionError | 'images.error.uploadFailed' };

/**
 * Convierte y sube una imagen.
 *
 * **Nunca lanza**: devuelve el motivo. Quien la llama es un editor, y una excepción ahí se convierte
 * en una pantalla en blanco a mitad de preparar una clase.
 */
export async function uploadQuestionImage(
  file: File,
  quizId: string,
  usedBytes: number,
): Promise<UploadResult> {
  const converted = await convertImage(file, { usedBytes });
  if (!converted.ok) return { ok: false, reason: converted.reason };

  const path = `${quizId}/${crypto.randomUUID()}.webp`;

  try {
    const { error } = await supabase.storage.from(BUCKET).upload(path, converted.image.blob, {
      contentType: 'image/webp',
      // Sin sobrescribir: cada imagen tiene su identificador propio, así que una colisión sería un
      // fallo del que conviene enterarse, no algo que tapar.
      upsert: false,
    });

    if (error !== null) return { ok: false, reason: 'images.error.uploadFailed' };
  } catch {
    return { ok: false, reason: 'images.error.uploadFailed' };
  }

  return { ok: true, image: toUploaded(path, converted.image) };
}

function toUploaded(path: string, image: ConvertedImage): UploadedImage {
  return { path, width: image.width, height: image.height, bytes: image.blob.size };
}

/**
 * Una URL para mostrar la imagen.
 *
 * Devuelve nulo en lugar de lanzar: una imagen que no carga es un hueco en el enunciado, no una
 * pantalla rota.
 */
export async function signedImageUrl(path: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, SIGNED_URL_SECONDS);

    if (error !== null) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

/** Borra una imagen. Un fallo aquí deja una huérfana, que la purga semanal recoge. */
export async function removeQuestionImage(path: string): Promise<void> {
  try {
    await supabase.storage.from(BUCKET).remove([path]);
  } catch {
    // La imagen queda huérfana y la recoge la purga. Impedir guardar la pregunta por esto sería
    // mucho peor que dejar doscientos kilobytes sin dueño.
  }
}

/**
 * Cuánto ocupan ya las imágenes de esta persona.
 *
 * Se calcula **sumando lo que hay en el bucket**, no leyendo un contador. Un contador hay que
 * mantenerlo, y basta una subida que falle a medias o un borrado que no lo decremente para que
 * empiece a mentir; la suma no puede desincronizarse porque no es una copia de nada.
 *
 * La política de lectura del bucket ya limita lo que se ve a los cuestionarios propios, así que
 * esta consulta devuelve exactamente lo de quien pregunta sin filtrar por nada más.
 */
export async function usedImageBytes(): Promise<number> {
  try {
    const { data, error } = await supabase
      .schema('storage')
      .from('objects')
      .select('metadata')
      .eq('bucket_id', BUCKET);

    if (error !== null) return 0;

    return ((data ?? []) as { metadata: { size?: number } | null }[]).reduce(
      (total, row) => total + (row.metadata?.size ?? 0),
      0,
    );
  } catch {
    // Sin poder calcularla, se sigue: el tope duro por fichero vive en el bucket, que es el que de
    // verdad protege el disco.
    return 0;
  }
}
